/**
 * Structured error codes the backend returns in ``data.code`` for known
 * conditions. Frontend uses these to render context-aware UI / toasts.
 *
 * BE side: see ``backend/PlatformaAPP/<app>/errors.py`` — introduce a matching
 * DOMAIN error code (and an i18n message if needed) when adding a new entry
 * here.
 *
 * The global axios interceptor in ``services/httpClient.ts`` checks
 * ``extractErrorCode(data)`` first and picks a contextual message if the
 * payload contains a known code; otherwise it falls through to the generic
 * status-based logic.
 */
import { SLOT_COPY } from '@/constants/strings';

export const ERROR_CODES = {
    CARD_ALREADY_BOUND: 'card_already_bound',
    BOOKING_HOLD_FAILED: 'booking_hold_failed',
    PAYMENT_PROVIDER_UNAVAILABLE: 'payment_provider_unavailable',
    SLOT_TAKEN_BY_ANOTHER_USER: 'slot_taken_by_another_user',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/**
 * Extract a typed error code from a DRF response. Returns null if absent
 * (and narrows the result to ErrorCode when present).
 *
 * The backend convention is ``{"code": "<snake_case_code>", "detail": "..."}``;
 * the ``detail`` is still the human-readable string the interceptor falls
 * back to when no matching code is found.
 */
export function extractErrorCode(data: unknown): ErrorCode | null {
    if (data && typeof data === 'object' && 'code' in data) {
        const code = (data as { code: unknown }).code;
        if (typeof code === 'string') {
            return code as ErrorCode;
        }
    }
    return null;
}

/**
 * Contextual messages for known error codes. Used by the global interceptor
 * to render a user-friendly toast when the backend signals a typed condition.
 * Keep this in sync with ``ERROR_CODES`` above.
 */
export const ERROR_CODE_MESSAGES: Record<ErrorCode, string> = {
    [ERROR_CODES.CARD_ALREADY_BOUND]: 'Эта карта уже привязана к вашему профилю.',
    [ERROR_CODES.BOOKING_HOLD_FAILED]:
        'Не удалось зарезервировать слот. Попробуйте выбрать другое время.',
    [ERROR_CODES.PAYMENT_PROVIDER_UNAVAILABLE]:
        'Платёжный провайдер временно недоступен. Попробуйте позже.',
    [ERROR_CODES.SLOT_TAKEN_BY_ANOTHER_USER]:
        'Этот слот уже занят другим пользователем. Выберите другое время.',
};

/**
 * Translate a backend ``HoldConflictError``/``HoldExpiredError`` detail string
 * into the right user-facing message for the booking modal.
 *
 * The booking flow used to collapse every 409 from the hold endpoint into a
 * single «Этот слот уже занят» toast. That string lies in two of the four
 * real cases:
 *
 *   - «Slot start time already passed.» → the menti was late, not displaced.
 *   - «Hold TTL elapsed.» / «Hold expired before confirmation.» → the payment
 *     window closed, the slot is probably still free.
 *
 * BE owns the truth (``backend/PlatformaAPP/payments/workflows.py`` and
 * ``backend/PlatformaAPP/consultations/services/holds.py``); we substring-match
 * on ``detail`` because the BE strings are stable and grepped in tests. If a
 * new detail string lands without a matching branch here we fall back to the
 * generic message — the FE will never crash, only the granularity degrades.
 *
 * @param detail Raw ``response.data.detail`` from a 409. May be ``null``/empty.
 * @param slotStartIso Optional ISO start time of the slot the user clicked,
 *                     so we can show «Слот уже начался в 18:05 …» when known.
 * @param minBookingNoticeSeconds Optional value from
 *                     ``response.data.min_booking_notice_seconds`` when the BE
 *                     raises ``slot_starts_too_soon``. Used to quote the
 *                     mentor's exact notice threshold in hours.
 */
export function mapBookingHoldError(
    detail: string | null | undefined,
    slotStartIso?: string | null,
    minBookingNoticeSeconds?: number | null,
): string {
    const normalized = (detail ?? '').toString().toLowerCase().trim();

    if (!normalized) {
        return SLOT_COPY.holdGenericFallback;
    }

    // BE: payments/workflows.py / consultations/services/holds.py raises
    // ``slot_starts_too_soon`` with detail like
    //   «Slot starts in less than 4 часа. Pick a later slot.»
    // and payload ``min_booking_notice_seconds: 14400``. The "X hours" piece
    // of the detail is already localised on the BE — we cannot trust the
    // number that appears there to be the actual threshold (it is rendered
    // from the same field), so we re-derive hours from
    // ``min_booking_notice_seconds`` for safety and i18n cleanliness.
    if (normalized.includes('slot starts in less than')) {
        if (typeof minBookingNoticeSeconds === 'number' && minBookingNoticeSeconds > 0) {
            const hours = Math.round(minBookingNoticeSeconds / 3600);
            if (hours > 0) {
                return SLOT_COPY.slotStartsTooSoon(hours);
            }
        }
        return SLOT_COPY.slotStartsTooSoonGeneric;
    }

    // BE strings (case-insensitive substring match):
    //   - "Slot start time already passed."  (workflows.py:623, 660;
    //                                         consultations/services/holds.py:345, 470)
    //   - "Slot cancelled by mentor"         (holds.py:369) — also a "no longer
    //                                         available" condition, fold into
    //                                         the "already started" bucket so
    //                                         we don't blame the user.
    if (
        normalized.includes('already passed') ||
        normalized.includes('start time') ||
        normalized.includes('cancelled by mentor')
    ) {
        const timeHHmm = formatHHmm(slotStartIso);
        return timeHHmm
            ? SLOT_COPY.slotAlreadyStartedWithTime(timeHHmm)
            : SLOT_COPY.slotAlreadyStarted;
    }

    // 12.11 booking-layer guard (consultations/services/holds.py): the slot
    // overlaps something the mentor already committed to (e.g. one of their
    // events). The truthful copy is «Это время уже занято» — distinct from
    // «только что забронировали» (nobody else grabbed it). Match the greppable
    // BE phrase BEFORE the generic "already booked" bucket below.
    if (normalized.includes('occupied by the mentor')) {
        return SLOT_COPY.slotTimeUnavailable;
    }

    // "Slot already booked.", "Slot already on hold.", "Slot already booked
    // by another user.", "Slot no longer available." — somebody else has it.
    if (
        normalized.includes('already booked') ||
        normalized.includes('already on hold') ||
        normalized.includes('no longer available') ||
        normalized.includes('slot taken')
    ) {
        return SLOT_COPY.slotJustTaken;
    }

    // HoldExpiredError → "Hold TTL elapsed." / "Hold expired before
    // confirmation." — payment window closed; the slot is probably free.
    if (
        normalized.includes('expired') ||
        normalized.includes('ttl elapsed') ||
        normalized.includes('hold is not active')
    ) {
        return SLOT_COPY.holdExpired;
    }

    // У наставника нет активного способа выплат. Одна подстрока покрывает ОБА
    // серверных пути — legacy (`consultations/services/holds.py`,
    // SellerPayoutMissingError) и V2-checkout (`commerce/application/use_cases/
    // cutover.py`, CommerceCheckoutError): текст причины в них общий. Ветка
    // обязана стоять до финального fallback'а — он советует «попробуйте другой
    // слот», а здесь этот совет вреден: любой другой слот той же платной услуги
    // упрётся в тот же гейт.
    if (normalized.includes('способ получения оплаты')) {
        return SLOT_COPY.sellerPayoutMissing;
    }

    return SLOT_COPY.holdGenericFallback;
}

/**
 * Pull an ``HH:MM`` from an ISO 8601 timestamp without dragging moment into
 * a module that does not otherwise need it. Returns ``null`` when the input
 * is empty or malformed.
 */
function formatHHmm(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
}
